import { type Transform } from '@dnd-kit/utilities';
import { CSS } from '@dnd-kit/utilities';
import { type SxProps, type Theme } from '@mui/material';

import { customPalette } from '@shared/theme/StyleProvider';

type DraggableStyleOptions = {
  isDragOverlay: boolean;
  isBeingDragged: boolean;
  isSelected: boolean;
  transform: Transform | null;
  transition: string | undefined;
};

export const createDraggableBaseStyles =
  ({
    isDragOverlay,
    isBeingDragged,
    isSelected,
    transform,
    transition,
  }: DraggableStyleOptions) =>
  (theme: Theme) => ({
    backgroundColor: isSelected
      ? theme.palette.primary.lighter
      : theme.palette.background.paper,
    ...(transform && { transform: CSS.Transform.toString(transform) }),
    ...(transition != null && {
      transition: isBeingDragged ? 'none' : transition,
    }),
    opacity: isBeingDragged ? 0.5 : 1,
    '&:hover': {
      backgroundColor: isSelected
        ? theme.palette.action.selected
        : isDragOverlay
        ? theme.palette.background.paper
        : theme.palette.action.hover,
    },
  });

export const createListItemStyles = (
  options: DraggableStyleOptions & { isInteractive: boolean },
): SxProps<Theme> => {
  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: '12px',
    py: 1,
    ...(options.transform && {
      transform: CSS.Transform.toString(options.transform),
    }),
  };

  // For drag overlay, return a plain object with hardcoded colors
  if (options.isDragOverlay) {
    return (theme: Theme) => ({
      ...baseStyles,
      backgroundColor: options.isSelected ? '#EEF2FF' : '#FFFFFF',
      transition: 'none',
      opacity: 1,
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
      cursor: 'pointer',
      '.dark &': {
        backgroundColor: options.isSelected
          ? customPalette.blue50
          : (theme.vars ?? theme).palette.background.paper,
        borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
      },
    });
  }

  // For normal rendering, return a function that uses theme
  return (theme: Theme) => ({
    ...createDraggableBaseStyles(options)(theme),
    ...baseStyles,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    cursor: options.isInteractive ? 'pointer' : 'default',
  });
};

export const SMALL_CHIP_SX: SxProps = {
  height: 20,
  fontSize: '0.75rem',
  '& .MuiChip-icon': {
    fontSize: '0.75rem',
  },
};

export const createGridItemStyles = (
  options: DraggableStyleOptions & {
    isDragging: boolean;
    isMobileOrTablet: boolean;
  },
): SxProps<Theme> => {
  const baseStyles = {
    position: 'relative',
    borderRadius: 2,
    overflow: 'hidden',
    minWidth: 215,
    ...(options.transform && {
      transform: CSS.Transform.toString(options.transform),
    }),
  };

  // For drag overlay, return a plain object with hardcoded colors
  if (options.isDragOverlay) {
    return (theme: Theme) => ({
      ...baseStyles,
      backgroundColor: options.isSelected ? '#EEF2FF' : '#FFFFFF',
      transition: 'none',
      opacity: 1,
      cursor: 'grabbing',
      boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
      ...(!options.isMobileOrTablet && {
        '& .image-actions': { opacity: 0 },
      }),
      '.dark &': {
        backgroundColor: options.isSelected
          ? customPalette.blue50
          : (theme.vars ?? theme).palette.background.paper,
      },
    });
  }

  // For normal rendering, return a function that uses theme
  return (theme: Theme) => {
    const draggableBase = createDraggableBaseStyles(options)(theme);
    const styles: any = {
      ...draggableBase,
      ...baseStyles,
      cursor: options.isDragging ? 'grabbing' : 'pointer',
      boxShadow: 'none',
      border: `1px solid ${
        options.isSelected ? theme.palette.primary.main : theme.palette.divider
      }`,
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        ...(draggableBase['&:hover'] as Record<string, unknown>),
        borderColor: options.isSelected
          ? theme.palette.primary.main
          : theme.palette.text.disabled,
        '& .image-actions': { opacity: 1 },
      },
    };

    if (options.isMobileOrTablet) {
      styles['& .image-actions'] = { opacity: 1 };
    }

    return styles;
  };
};
